/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, OnInit } from '@angular/core';
import { PrintService } from '../print.service';
import { NEVER, Observable } from 'rxjs';
import { PdfDialogComponent } from '../../../../../core/ui/pdf/pdf-dialog.component';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { LoggerService } from '../../../../shared/services/logger.service';
import { PrintOptionsState } from '../state/print-options.state';
import { Select, Store } from '@ngxs/store';
import { MapPrintOptions, PaperSize, PdfPrintResult } from '../state/print-options.model';
import { concatMap, first, tap } from 'rxjs/operators';
import { SetPrintOptions } from '../state/print-options.actions';
import { AppConfigState } from '../../../../../core/state/appConfig/app-config.state';
import { UntypedFormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { DeviceService } from '../../../../shared/services/device.service';

export class PrintErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

@Component({
    selector: 'dash-gis-print-dialog',
    templateUrl: './print-dialog.component.html',
    styleUrls: [ './print-dialog.component.scss' ],
    providers: [ PrintService ],
})
export class PrintDialogComponent implements OnInit {

    public printSelectMatcher = new PrintErrorMatcher();

    @Select(AppConfigState.print)
    public printConfig$: Observable<any>;

    @Select(PrintOptionsState.paperSize)
    public paperSize$: Observable<PaperSize>;

    @Select(PrintOptionsState.showLegend)
    public showLegend$: Observable<boolean>;

    @Select(PrintOptionsState.showTitle)
    public showTitle$: Observable<boolean>;

    @Select(PrintOptionsState.titleText)
    public titleText$: Observable<string>;

    @Select(PrintOptionsState.showNorth)
    public showNorth$: Observable<boolean>;

    public printing = false;

    @Select(PrintOptionsState.options) private printSettings$: Observable<MapPrintOptions>;

    constructor(
        private logger: LoggerService,
        private printService: PrintService,
        private deviceService: DeviceService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<PrintDialogComponent>,
        private store: Store,
    ) {}

    ngOnInit() {
        this.paperSize$.pipe(
            tap((paperSize: PaperSize) => {
                if (!paperSize || !paperSize.value || !paperSize.value.name) {
                    return this.setPaperSize(this.paperOptions[0]);
                } else {
                    return NEVER;
                }
            }),
            first(),
        ).subscribe();
    }

    public print() {
        this.printing = true;
        this.printSettings$.pipe(
            concatMap((settings: MapPrintOptions) => {
                const opts: MapPrintOptions = {
                    highQuality: settings.highQuality,
                    paperSize: settings.paperSize,
                    showLegend: settings.showLegend,
                    showNorth: settings.showNorth,
                    showTitle: settings.showTitle,
                    titleText: settings.titleText,
                };
                return this.printService.pdfPrint(opts);
            }),
        ).subscribe((result: PdfPrintResult) => {
            this.openPdfDialog(result);
            this.printing = false;
            this.dialogRef.close();
        });
    }

    public cancel() {
        this.printService.cancelPrint();
        this.dialogRef.close();
    }

    public paperCompareFn(p1: PaperSize, p2: PaperSize) {
        return (p1 && p2) ? p1.value.name === p2.value.name : p1 === p2;
    }

    public setShowTitle(value) {
        this.store.dispatch(new SetPrintOptions({showTitle: value}));
    }

    public setTitleText(value) {
        this.store.dispatch(new SetPrintOptions({titleText: value}));
    }

    public setShowLegend(value) {
        this.store.dispatch(new SetPrintOptions({showLegend: value}));
    }

    public setShowNorth(value) {
        this.store.dispatch(new SetPrintOptions({showNorth: value}));
    }

    public setPaperSize(value) {
        this.store.dispatch(new SetPrintOptions({paperSize: value}));
    }

    private openPdfDialog(result: PdfPrintResult) {
        const pdfConfig: MatDialogConfig = {
            data: result
        };

        // if (!this.deviceService.isIOS) {
            pdfConfig.width = '80vw';
            pdfConfig.height = '80vh';
            pdfConfig.panelClass = 'print-pdf-container';
        // }
        this.dialog.open(PdfDialogComponent, pdfConfig);
    }

    // region Properties
    get progress$() {
        return this.printService.progress$;
    }

    get paperOptions() {
        return this.printService.paperOptions;
    }

    // endregion
}
